import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Shield, Leaf, HardHat } from 'lucide-react';

// ISO Standards configuration - easily extensible for future standards
export const ISO_STANDARDS = {
  'iso9001': {
    id: 'iso9001',
    code: 'ISO 9001',
    name: 'Quality Management System',
    fullName: 'ISO 9001:2015',
    icon: Shield,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    description: i18n.t('auto.lbl.qualityManagementSystemRequirements'),
    enabled: true,
  },
  'iso14001': {
    id: 'iso14001',
    code: 'ISO 14001',
    name: 'Environmental Management',
    fullName: 'ISO 14001:2015',
    icon: Leaf,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    description: i18n.t('auto.lbl.environmentalManagementSystemRequirements'),
    enabled: false, // Coming soon
  },
  'iso45001': {
    id: 'iso45001',
    code: 'ISO 45001',
    name: 'Occupational Health & Safety',
    fullName: 'ISO 45001:2018',
    icon: HardHat,
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
    description: i18n.t('auto.lbl.occupationalHealthAndSafetyManagementSystem'),
    enabled: false, // Coming soon
  },
};

// Get all standards as array
export const getAllStandards = () => Object.values(ISO_STANDARDS);

// Get enabled standards
export const getEnabledStandards = () => Object.values(ISO_STANDARDS).filter(s => s.enabled);

// Get standard by ID
export const getStandard = (id) => ISO_STANDARDS[id] || ISO_STANDARDS['iso9001'];

// Standard selector component
const ISOStandardSelector = ({ 
  selectedStandard = 'iso9001', 
  onStandardChange, 
  showDisabled = false,
  className = '' 
}) => {
  const { t } = useTranslation();
  const standards = showDisabled ? getAllStandards() : getEnabledStandards();
  const current = getStandard(selectedStandard);
  const IconComponent = current.icon;

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <Select value={selectedStandard} onValueChange={onStandardChange}>
        <SelectTrigger className="w-[220px]" data-testid="iso-standard-selector">
          <div className="flex items-center gap-2">
            <IconComponent className={`h-4 w-4 ${current.color}`} />
            <SelectValue />
          </div>
        </SelectTrigger>
        <SelectContent>
          {standards.map((std) => {
            const Icon = std.icon;
            return (
              <SelectItem 
                key={std.id} 
                value={std.id}
                disabled={!std.enabled}
              >
                <div className="flex items-center gap-2">
                  <Icon className={`h-4 w-4 ${std.color}`} />
                  <span>{std.code}</span>
                  {!std.enabled && (
                    <Badge variant="outline" className="text-xs ml-2">{t('auto.iso.soon')}</Badge>
                  )}
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
      
      {/* Standard info badge */}
      <Badge className={`${current.bgColor} ${current.color} border-0`}>
        {current.name}
      </Badge>
    </div>
  );
};

// Standard badge component for displaying in lists
export const ISOStandardBadge = ({ standardId, showName = false }) => {
  const std = getStandard(standardId);
  const Icon = std.icon;
  
  return (
    <Badge className={`${std.bgColor} ${std.color} border-0 flex items-center gap-1`}>
      <Icon className="h-3 w-3" />
      {showName ? std.name : std.code}
    </Badge>
  );
};

export default ISOStandardSelector;
